export interface FrPdfToolMeta {
  slug: string;
  enSlug: string;
  name: string;
  tagline: string;
  description: string;
  category: "Organiser & Modifier" | "Optimiser & Réparer" | "Convertir en PDF" | "Convertir depuis PDF" | "Sécurité & Signatures";
  icon: string;
  featured?: boolean;
  keywords: string[];
}

export const FR_PDF_CATEGORIES = [
  "Organiser & Modifier",
  "Optimiser & Réparer",
  "Convertir en PDF",
  "Convertir depuis PDF",
  "Sécurité & Signatures",
] as const;

export type FrPdfCategory = (typeof FR_PDF_CATEGORIES)[number];

export const FR_PDF_TOOLS: FrPdfToolMeta[] = [
  // Organiser & Modifier
  {
    slug: "fusionner-pdf",
    enSlug: "merge-pdf",
    name: "Fusionner des PDF en Ligne Gratuitement",
    tagline: "Combinez plusieurs fichiers PDF en un seul document clair",
    description: "Assemblez deux ou plusieurs documents PDF en un fichier unique. Choisissez l'ordre des pages et téléchargez votre fichier rapidement et en toute confidentialité.",
    category: "Organiser & Modifier",
    icon: "FilePlus",
    featured: true,
    keywords: ["fusionner pdf", "assembler pdf gratuit", "combiner plusieurs pdf", "joindre fichiers pdf"],
  },
  {
    slug: "diviser-pdf",
    enSlug: "split-pdf",
    name: "Diviser un PDF & Séparer les Pages",
    tagline: "Extrayez des pages ou découpez votre PDF en plusieurs fichiers",
    description: "Séparez facilement des pages individuelles ou des plages de pages de vos documents PDF.",
    category: "Organiser & Modifier",
    icon: "Scissors",
    featured: true,
    keywords: ["diviser pdf", "separer pages pdf", "decouper fichier pdf gratuit"],
  },
  {
    slug: "organiser-pdf",
    enSlug: "organize-pdf",
    name: "Organiser & Réorganiser les Pages d'un PDF",
    tagline: "Triez, supprimez et pivotez vos pages PDF par glisser-déplacer",
    description: "Réorganisez visuellement les pages d'un fichier PDF, supprimez les pages inutiles et faites pivoter les feuillets.",
    category: "Organiser & Modifier",
    icon: "LayoutGrid",
    featured: true,
    keywords: ["organiser pdf", "trier pages pdf", "reorganiser ordre pages pdf"],
  },
  {
    slug: "pivoter-pdf",
    enSlug: "rotate-pdf",
    name: "Pivoter un PDF en Ligne",
    tagline: "Tournez une page ou l'ensemble de votre PDF à 90°, 180° ou 270°",
    description: "Corrigez l'orientation de vos documents PDF mal scannés ou orientés à l'envers.",
    category: "Organiser & Modifier",
    icon: "RotateCw",
    keywords: ["pivoter pdf", "tourner page pdf", "orienter pdf en ligne"],
  },
  {
    slug: "extraire-pages-pdf",
    enSlug: "extract-pages",
    name: "Extraire des Pages d'un PDF",
    tagline: "Enregistrez les pages sélectionnées dans un nouveau document",
    description: "Sélectionnez des pages spécifiques au sein d'un document PDF lourd et exportez-les séparément.",
    category: "Organiser & Modifier",
    icon: "FileOutput",
    keywords: ["extraire pages pdf", "enregistrer certaines pages pdf", "extraire feuillet pdf"],
  },
  {
    slug: "supprimer-pages-pdf",
    enSlug: "remove-pages",
    name: "Supprimer des Pages d'un PDF",
    tagline: "Retirez les pages vierges ou superflues de vos fichiers",
    description: "Effacez définitivement les pages indésirables de vos documents directement dans votre navigateur.",
    category: "Organiser & Modifier",
    icon: "Trash2",
    keywords: ["supprimer pages pdf", "effacer page pdf", "enlever page inutile pdf"],
  },
  {
    slug: "modifier-pdf",
    enSlug: "edit-pdf",
    name: "Modifier un PDF en Ligne Gratuitement",
    tagline: "Ajoutez du texte, des signatures, des formes et des images",
    description: "Insérez des annotations, des zones de texte, du dessin libre, des signatures électroniques et des images dans vos PDF.",
    category: "Organiser & Modifier",
    icon: "Edit3",
    featured: true,
    keywords: ["modifier pdf", "editer un pdf en ligne gratuit", "ajouter texte sur pdf"],
  },
  {
    slug: "numeroter-pages-pdf",
    enSlug: "page-numbers",
    name: "Ajouter des Numéros de Page dans un PDF",
    tagline: "Insérez des numéros de page personnalisés en en-tête ou pied de page",
    description: "Numérotez automatiquement les pages de vos PDF avec position, taille de police et formatage sur mesure.",
    category: "Organiser & Modifier",
    icon: "Hash",
    keywords: ["numeroter pages pdf", "ajouter numero de page pdf", "numerotation pdf"],
  },
  {
    slug: "filigrane-pdf",
    enSlug: "watermark-pdf",
    name: "Ajouter un Filigrane dans un PDF",
    tagline: "Apposez un filigrane texte ou un logo sur vos pages",
    description: "Protégez vos documents confidentiels en ajoutant un tampon filigrane texte ou image.",
    category: "Organiser & Modifier",
    icon: "Droplets",
    keywords: ["filigrane pdf", "ajouter filigrane sur pdf", "tatouage numerique pdf"],
  },

  // Optimiser & Réparer
  {
    slug: "compresser-pdf",
    enSlug: "compress-pdf",
    name: "Compresser un PDF & Réduire sa Taille",
    tagline: "Réduisez la taille de votre fichier PDF sans perte de lisibilité",
    description: "Comprimez vos documents PDF trop lourds pour un envoi facile par e-mail et sur vos formulaires en ligne.",
    category: "Optimiser & Réparer",
    icon: "Archive",
    featured: true,
    keywords: ["compresser pdf", "reduire taille pdf gratuit", "diminuer poids fichier pdf"],
  },
  {
    slug: "pdf-noir-et-blanc",
    enSlug: "grayscale-pdf",
    name: "Convertir un PDF en Noir et Blanc (Niveaux de Gris)",
    tagline: "Transformez vos PDF couleur en niveaux de gris pour l'impression",
    description: "Convertissez vos fichiers PDF en noir et blanc afin d'économiser l'encre et le toner de votre imprimante.",
    category: "Optimiser & Réparer",
    icon: "SunMedium",
    keywords: ["pdf noir et blanc", "convertir pdf en niveaux de gris", "pdf impression economique"],
  },
  {
    slug: "reparer-pdf",
    enSlug: "repair-pdf",
    name: "Réparer un PDF Endommagé",
    tagline: "Restaurez des fichiers PDF corrompus ou illisibles",
    description: "Récupérez le contenu et la structure des documents PDF endommagés directement depuis votre navigateur.",
    category: "Optimiser & Réparer",
    icon: "Wrench",
    keywords: ["reparer pdf", "restaurer fichier pdf corrompu", "recuperer pdf endommage"],
  },

  // Convertir en PDF
  {
    slug: "image-en-pdf",
    enSlug: "image-to-pdf",
    name: "Convertir Image en PDF (JPG, PNG en PDF)",
    tagline: "Transformez des images JPG, PNG, WEBP en document PDF",
    description: "Convertissez et regroupez plusieurs photos et images dans un document PDF proprement mis en page.",
    category: "Convertir en PDF",
    icon: "Image",
    featured: true,
    keywords: ["convertir image en pdf", "jpg en pdf gratuit", "png en pdf en ligne"],
  },
  {
    slug: "word-en-pdf",
    enSlug: "word-to-pdf",
    name: "Convertir Word en PDF (DOCX en PDF)",
    tagline: "Transformez vos documents Word en PDF de haute qualité",
    description: "Convertissez vos fichiers DOCX et DOC en documents PDF professionnels en conservant la mise en forme.",
    category: "Convertir en PDF",
    icon: "FileText",
    keywords: ["word en pdf", "convertir docx en pdf gratuit", "passer un document word en pdf"],
  },
  {
    slug: "excel-en-pdf",
    enSlug: "excel-to-pdf",
    name: "Convertir Excel en PDF (XLSX en PDF)",
    tagline: "Transformez vos feuilles de calcul Excel en documents PDF",
    description: "Convertissez vos classeurs Excel en fichiers PDF clairs, prêts à être imprimés ou partagés.",
    category: "Convertir en PDF",
    icon: "Table",
    keywords: ["excel en pdf", "convertir xlsx en pdf", "tableau excel en pdf"],
  },
  {
    slug: "powerpoint-en-pdf",
    enSlug: "ppt-to-pdf",
    name: "Convertir PowerPoint en PDF (PPTX en PDF)",
    tagline: "Transformez vos présentations PPTX en PDF compact",
    description: "Convertissez des diapositives PowerPoint en fichier PDF facile à transmettre et à lire.",
    category: "Convertir en PDF",
    icon: "Presentation",
    keywords: ["powerpoint en pdf", "convertir pptx en pdf", "presentation en pdf"],
  },
  {
    slug: "html-en-pdf",
    enSlug: "html-to-pdf",
    name: "Convertir HTML en PDF (Page Web en PDF)",
    tagline: "Enregistrez du code HTML ou des pages Web au format PDF",
    description: "Transformez votre code HTML et contenu web en un fichier PDF téléchargeable.",
    category: "Convertir en PDF",
    icon: "Code",
    keywords: ["html en pdf", "convertir page web en pdf", "enregistrer site en pdf"],
  },
  {
    slug: "texte-en-pdf",
    enSlug: "txt-to-pdf",
    name: "Convertir Fichier Texte TXT en PDF",
    tagline: "Convertissez vos fichiers texte brut (.txt) en documents PDF",
    description: "Transformez du texte brut en document PDF mis en forme avec choix des polices.",
    category: "Convertir en PDF",
    icon: "FileType",
    keywords: ["txt en pdf", "convertir texte en pdf", "fichier txt en pdf"],
  },

  // Convertir depuis PDF
  {
    slug: "pdf-en-image",
    enSlug: "pdf-to-image",
    name: "Convertir PDF en Image (PDF en JPG / PNG)",
    tagline: "Extrayez chaque page d'un PDF sous forme d'image HD",
    description: "Convertissez les pages d'un fichier PDF en images haute résolution JPG ou PNG.",
    category: "Convertir depuis PDF",
    icon: "FileImage",
    featured: true,
    keywords: ["pdf en image", "convertir pdf en jpg gratuit", "enregistrer pdf sous forme d image"],
  },
  {
    slug: "pdf-en-word",
    enSlug: "pdf-to-word",
    name: "Convertir PDF en Word (PDF en DOCX)",
    tagline: "Extrayez le texte et convertissez votre PDF en fichier Word modifiable",
    description: "Convertissez votre document PDF en fichier Word (.docx) rééditable en préservant le texte.",
    category: "Convertir depuis PDF",
    icon: "FileCode",
    keywords: ["pdf en word", "convertir pdf en docx modifiable", "pdf vers word gratuit"],
  },
  {
    slug: "pdf-en-excel",
    enSlug: "pdf-to-excel",
    name: "Convertir PDF en Excel (PDF en XLSX)",
    tagline: "Extrayez les tableaux d'un PDF vers des feuilles de calcul",
    description: "Exportez les données tabulaires de vos PDF directement dans des feuilles de calcul Excel rééditables.",
    category: "Convertir depuis PDF",
    icon: "Spreadsheet",
    keywords: ["pdf en excel", "convertir tableau pdf en excel", "extraire donnees pdf vers xlsx"],
  },
  {
    slug: "extraire-texte-pdf",
    enSlug: "pdf-to-text",
    name: "Extraire le Texte d'un PDF en TXT",
    tagline: "Récupérez tout le contenu textuel brut d'un fichier PDF",
    description: "Hachez et réservez le texte brut de votre PDF dans un fichier .txt ultra léger.",
    category: "Convertir depuis PDF",
    icon: "FileLines",
    keywords: ["extraire texte pdf", "recuperer texte d un pdf", "copier tout le texte du pdf"],
  },

  // Sécurité & Signatures
  {
    slug: "proteger-pdf",
    enSlug: "protect-pdf",
    name: "Protéger un PDF par Mot de Passe",
    tagline: "Chiffrez et sécurisez votre document PDF avec un mot de passe",
    description: "Sécurisez vos fichiers PDF confidentiels avec un chiffrement fort et un mot de passe d'accès.",
    category: "Sécurité & Signatures",
    icon: "Lock",
    featured: true,
    keywords: ["proteger pdf", "pdf mot de passe", "verrouiller fichier pdf", "chiffrer un pdf"],
  },
  {
    slug: "deverrouiller-pdf",
    enSlug: "unlock-pdf",
    name: "Déverrouiller un PDF (Enlever le Mot de Passe)",
    tagline: "Retirez la protection par mot de passe de vos fichiers PDF",
    description: "Supprimez le mot de passe et les restrictions d'édition de vos documents PDF légitimes.",
    category: "Sécurité & Signatures",
    icon: "Unlock",
    keywords: ["deverrouiller pdf", "enlever mot de passe pdf", "supprimer protection pdf"],
  },
  {
    slug: "signer-pdf",
    enSlug: "sign-pdf",
    name: "Signer un PDF en Ligne (Signature Électronique)",
    tagline: "Ajoutez une signature manuscrite ou numérique sur vos documents",
    description: "Dessinez votre signature ou téléversez une image de signature pour signer vos contrats et formulaires PDF.",
    category: "Sécurité & Signatures",
    icon: "PenTool",
    featured: true,
    keywords: ["signer pdf", "signature electronique pdf", "signer contrat pdf en ligne gratuit"],
  },
  {
    slug: "ocr-pdf",
    enSlug: "ocr-pdf",
    name: "Reconnaissance de Texte OCR PDF",
    tagline: "Rendez vos documents PDF scannés recherchables",
    description: "Reconnaissance optique de caractères (OCR) pour extraire et rendre le texte sélectionnable sur un scan.",
    category: "Sécurité & Signatures",
    icon: "ScanText",
    keywords: ["ocr pdf", "reconnaissance de texte pdf scanne", "rendre pdf chercheable"],
  },
  {
    slug: "generateur-code-qr",
    enSlug: "qr-code",
    name: "Générateur de Code QR",
    tagline: "Créez des codes QR personnalisés pour liens, WiFi, vCards & texte",
    description: "Générez des codes QR personnalisés pour sites web, réseaux WiFi, cartes de visite et textes. Téléchargez-les en PNG ou tamponnez-les directement sur un PDF.",
    category: "Sécurité & Signatures",
    icon: "QrCode",
    featured: true,
    keywords: ["generateur code qr", "creer code qr gratuit", "code qr wifi", "code qr url"],
  },
];

export function getFrPdfToolBySlug(slug: string): FrPdfToolMeta | undefined {
  return FR_PDF_TOOLS.find((t) => t.slug === slug || t.enSlug === slug);
}

export function getEnPdfSlugForFrSlug(frSlug: string): string {
  const found = FR_PDF_TOOLS.find((t) => t.slug === frSlug || t.enSlug === frSlug);
  return found ? found.enSlug : frSlug;
}

export function getFrPdfSlugForEnSlug(enSlug: string): string {
  const found = FR_PDF_TOOLS.find((t) => t.enSlug === enSlug || t.slug === enSlug);
  return found ? found.slug : enSlug;
}

export function relatedFrPdfTools(currentSlug: string, count = 4): FrPdfToolMeta[] {
  const current = getFrPdfToolBySlug(currentSlug);
  return FR_PDF_TOOLS.filter((t) => t.slug !== current?.slug).slice(0, count);
}
